import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { errorStatus } from '../lib/httpError';
import { getAdminPendingCounts, getVersion, logout } from '../api/client';
import { useToast } from '../hooks/useToast';
import { useBusy } from '../hooks/useBusy';
import Avatar from './shared/Avatar';
import { GearIcon, QuestionMarkIcon, RefreshIcon, ScaleIcon } from './shared/Icons';
import { ALL_OFFICIAL_STATS_ROLES, OFFICIAL_STATS_ROLES } from '../utils/officialStats';
import { logBackgroundError } from '../observability';
import { PENDING_COUNTS_EVENT } from '../lib/pendingCounts';
import NotificationBell from './NotificationBell';
import PushPromptBanner from './PushPromptBanner';
import { APP_ENV } from '../utils/appEnv';
import { BADGE_XXS_TALL } from './shared/badgeSize';

/** #499 — the installed app (Add to Home Screen) has no address bar, no
 *  reload control and no pull-to-refresh. */
function isInstalledApp(): boolean {
  const standalone = typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari marks its home-screen app with a non-standard flag.
  return standalone || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, hasRole } = useAuthStore();
  const { t, lang, setLang } = useI18n();
  const { showToast } = useToast();
  /** #499 — offline, a full page load of the installed app shows an error
   *  page with no controls: the service worker keeps no page. Load again
   *  when the server answers at all. Any answer, a 5xx or a 429 too, proves
   *  a network, and the page itself comes from the frontend app. The check
   *  can take 10 s on a WiFi with no internet (the GET retries), so the
   *  button shows that it works and takes no second tap. */
  const [checking, setChecking] = useState(false);
  const reloadPage = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await getVersion({ timeout: 3_000 });
    } catch (err) {
      if (errorStatus(err) === undefined) {
        setChecking(false);
        showToast(t.app.reloadOffline, 'warning');
        return;
      }
    }
    window.location.reload();
  };
  const location = useLocation();
  const navigate = useNavigate();
  // #666 — on a stalled network a sign-out can take about 23 s (the wait for
  // a refresh, the push release, the POST). The button shows the busy label.
  // One sign-out runs at a time (`logout()`), so a second tap on this button
  // or on the button of Profile sends nothing.
  const signOut = useBusy();
  const [adminPendingCount, setAdminPendingCount] = useState(0);
  const [installedApp] = useState(isInstalledApp);
  const isTeamManager = hasRole('admin', 'team_manager');
  // #616 — the statistics of the officials: the list for an admin, the
  // tournament_manager role and the jury role; the own page for a referee.
  const canSeeOfficialStats = hasRole(...OFFICIAL_STATS_ROLES);
  const canSeeAllOfficialStats = hasRole(...ALL_OFFICIAL_STATS_ROLES);
  const officialsTo = canSeeAllOfficialStats || !user ? '/officials' : `/officials/${user.id}`;

  // Admin/TM only: fetch pending-approval count on mount + route change.
  // No poll — admin queue isn't time-critical and visiting /admin is the
  // natural ack moment (next route change refreshes the badge).
  // #664 — on /admin the route does not change while the admin answers
  // requests. The Admin page reads the counter again after each answer and
  // announces the total (PENDING_COUNTS_EVENT), so the badge and the tab agree.
  // A read that began before the announcement is dropped: it is older.
  const countsRead = useRef(0);
  useEffect(() => {
    if (!user || !isTeamManager) { countsRead.current++; setAdminPendingCount(0); return; }
    if (!navigator.onLine) return;
    const run = ++countsRead.current;
    getAdminPendingCounts()
      .then(r => { if (run === countsRead.current) setAdminPendingCount(r.data.total); })
      .catch(logBackgroundError('Layout:admin-pending-counts'));
  }, [user?.id, isTeamManager, location.pathname]);
  useEffect(() => {
    if (!user || !isTeamManager) return;
    const onAnnounce = (e: Event) => {
      const total = (e as CustomEvent<number>).detail;
      if (typeof total !== 'number') return;
      countsRead.current++;
      setAdminPendingCount(total);
    };
    window.addEventListener(PENDING_COUNTS_EVENT, onAnnounce);
    return () => window.removeEventListener(PENDING_COUNTS_EVENT, onAnnounce);
  }, [user?.id, isTeamManager]);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/tournaments', label: t.nav.tournaments },
    { to: '/teams', label: t.nav.teams },
    { to: '/athletes', label: t.nav.athletes },
    ...(canSeeOfficialStats ? [{ to: officialsTo, label: t.nav.officials }] : []),
    { to: '/manual', label: t.nav.help },
    ...(isTeamManager ? [{ to: '/admin', label: (
      <span className="inline-flex items-center gap-1.5">
        <GearIcon className="w-3.5 h-3.5" /> Admin
        {adminPendingCount > 0 && (
          <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {adminPendingCount > 9 ? '9+' : adminPendingCount}
          </span>
        )}
      </span>
    ) }] : []),
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          {/* Logo + full name */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0">
            <img src="/logo.webp" alt="LMF" className="h-8 w-8 rounded-full object-cover bg-white p-0.5 shrink-0" />
            {/* #572 — from lg to xl the header nav takes the room, so the bar
                shows the short name there too. At 1024 px the full name made
                the admin bar scroll sideways, by about 40 px in Lithuanian. */}
            <div className="min-w-0 hidden sm:block lg:hidden xl:block">
              <div className="text-sm font-black text-gray-900 leading-tight truncate">
                {t.nav.federationName}
              </div>
            </div>
            {/* Phone, and lg to xl: the short name */}
            <span className="text-sm font-black text-gray-900 sm:hidden lg:inline xl:hidden">{t.nav.federationShort}</span>
            {/* Environment pill — inside Link, next to app name */}
            {(() => {
              if (APP_ENV === 'development') return <span data-testid="env-pill" className={`badge ${BADGE_XXS_TALL} bg-purple-100 text-purple-700 border-purple-300 font-bold max-[359px]:hidden`}>Development</span>;
              if (APP_ENV === 'staging') return <span data-testid="env-pill" className={`badge ${BADGE_XXS_TALL} bg-amber-100 text-amber-700 border-amber-300 font-bold max-[359px]:hidden`}>Staging</span>;
              return null;
            })()}
          </Link>

          <nav className="hidden lg:flex items-center gap-1 mx-4">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to}
                // #616 — the Officials item: from lg to xl the links are narrower,
                // so the admin bar fits at 1024 px also in Lithuanian.
                className={`relative px-2 xl:px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(l.to)
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {l.label}
                {/* Notification count now shown on bell icon */}
              </Link>
            ))}
          </nav>

          {/* #572 — one standard for the controls of the bar: btn-header
              (index.css), 36 px on one centre line. Sign in and Sign out are
              36 px too (min-h-9). */}
          <div className="flex items-center gap-1 shrink-0">
            {/* #499 — a full page load, as pull-to-refresh does in a browser. */}
            {installedApp && (
              <button type="button" onClick={reloadPage} disabled={checking} aria-busy={checking}
                aria-label={t.app.reload} title={t.app.reload}
                className="btn-header">
                <RefreshIcon className={`w-5 h-5 ${checking ? 'animate-spin' : ''}`} />
              </button>
            )}
            {/* #552 — below lg the header nav is hidden, and the bottom nav
                has no Help tab: Help is this icon. */}
            <Link to="/manual" aria-label={t.nav.help} title={t.nav.help}
              aria-current={isActive('/manual') ? 'page' : undefined}
              className="btn-header lg:hidden">
              <QuestionMarkIcon className="w-5 h-5" />
            </Link>
            {/* Notification bell: badge, dropdown, push toggle, live socket delivery */}
            {user && <NotificationBell />}
            {/* #572 — the box of the other controls, with no border. The text
                stays exactly LT or EN: specs find the button by that name. */}
            <button onClick={() => setLang(lang === 'en' ? 'lt' : 'en')}
              className="btn-header">
              {lang === 'en' ? 'LT' : 'EN'}
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/profile"
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                  <Avatar firstName={user.first_name} lastName={user.last_name} photoUrl={user.photo_url} size="sm" />
                  {/* #572 — no name from lg to xl, where the header nav takes the room. */}
                  <span className="hidden md:block lg:hidden xl:block">{user.first_name}</span>
                </Link>
                <button onClick={() => signOut.run(async () => {
                  const { complete, retryAtNextLoad } = await logout();
                  // #666 — The POST got no 2xx answer. The page goes to Home now, so the
                  // warning stays until the user closes it. It names the resend of the next
                  // page load only when the storage kept the key.
                  if (!complete) {
                    showToast(retryAtNextLoad ? t.nav.signOutIncomplete : t.nav.signOutIncompleteNoRetry, 'warning', { untilClosed: true });
                  }
                  navigate('/');
                })}
                  disabled={signOut.busy}
                  className="hidden md:inline-flex btn-ghost btn-sm min-h-9 cursor-pointer">
                  {signOut.busy ? t.nav.signingOut : t.nav.logout}
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-primary btn-sm min-h-9">{t.nav.login}</Link>
            )}
          </div>
        </div>
      </header>
      <PushPromptBanner />

      {/* Mobile bottom nav. #552 — 5 tabs: 72 px columns at 360 px, so the
          12 px label "Tournaments" fits. Help is an icon in the header.
          #616 — the Officials tab makes 6 for the roles that see it
          (operator, 2026-10-17): 60 px columns and 10 px labels. */}
      <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-40">
        <div className={`grid h-14 ${canSeeOfficialStats ? 'grid-cols-6' : 'grid-cols-5'}`}>
          {([
            { to: '/', svg: 'M10.707 2.293a1 1 0 00-1.414 0l-7 7A1 1 0 003 11h1v6a1 1 0 001 1h4v-4h2v4h4a1 1 0 001-1v-6h1a1 1 0 00.707-1.707l-7-7z', label: t.nav.home },
            { to: '/tournaments', svg: 'M6 2a1 1 0 00-1 1v1H3.5A1.5 1.5 0 002 5.5v1A3.5 3.5 0 005.06 10 5 5 0 009 12.9V15H7a1 1 0 100 2h6a1 1 0 100-2h-2v-2.1A5 5 0 0014.94 10 3.5 3.5 0 0018 6.5v-1A1.5 1.5 0 0016.5 4H15V3a1 1 0 00-1-1H6zm0 3h8v3a4 4 0 01-8 0V5zM4 5.5a.5.5 0 01.5-.5H5v3a5 5 0 00.05.5A1.5 1.5 0 014 6.5v-1zm12 0v1a1.5 1.5 0 01-1.05 1.43c.033-.16.05-.32.05-.48V5h.5a.5.5 0 01.5.5z', label: t.nav.tournaments },
            { to: '/teams', svg: 'M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.97 5.97 0 00-.75-2.906A3.005 3.005 0 0119 17v1h-3zM4.75 14.094A5.97 5.97 0 004 17v1H1v-1a3 3 0 013.75-2.906z', label: t.nav.teams },
            { to: '/athletes', svg: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z', label: t.nav.athletes },
            ...(canSeeOfficialStats ? [{ to: officialsTo, svg: '', icon: <ScaleIcon className="w-5 h-5" />, label: t.nav.officials }] : []),
            ...(isTeamManager
              ? [{ to: '/admin', svg: 'M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z', label: 'Admin' }]
              : [{ to: '/profile', svg: 'M10 0a10 10 0 100 20 10 10 0 000-20zm0 4a3 3 0 110 6 3 3 0 010-6zm0 14a8 8 0 01-6.5-3.4c.1-2.4 4.1-3.6 6.5-3.6 2.4 0 6.4 1.2 6.5 3.6A8 8 0 0110 18z', label: t.nav.profile }]),
          ] as { to: string; svg: string; icon?: React.ReactNode; label: string }[]).map(l => (
            <Link key={l.to} to={l.to}
              className={`relative flex flex-col items-center justify-center gap-0.5 ${canSeeOfficialStats ? 'text-[10px]' : 'text-xs'} transition-colors ${
                isActive(l.to) ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
              <div className="relative">
                {l.icon ?? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d={l.svg}/></svg>}
                {l.to === '/admin' && adminPendingCount > 0 && (
                  <span className="absolute -top-1 -right-2 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {adminPendingCount > 9 ? '9+' : adminPendingCount}
                  </span>
                )}
              </div>
              {/* No side padding: it took 4 px of the 72 px column. The
                  truncate stays as a guard for a wide system font. */}
              <span className="truncate max-w-full tracking-tight">{l.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 pb-20 lg:pb-6">
        {children}
      </main>
    </div>
  );
}
